import React from 'react';
import config from 'config';
import { connect } from 'react-redux';
import { daysFrom } from 'helpers/dateUtils';
import { ISOString, parseISODate } from 'locale/formatDate';
import { useLocale } from 'locale';
import { DealType, PoiId } from 'utils/entities';
import { State } from 'store/state';
import { RelevancyLabelWrapper, ViewedLabelWrapper } from 'ds/components/ListingCard';
import { useABTests } from 'hooks/useABTests';
import { AddLastUpdateToWeights } from 'config/abTests';
import ViewedIcon from 'assets/svg/viewed.svg';
import { isInLastViewedSelector } from 'store/state/selectors/poi';
import { useSelector } from 'react-redux';
import { marketplaceSelector } from 'store/state/selectors/router';
import { MarketplaceType } from 'utils/entities';

export enum UnitRelevancy {
  New = 'new',
  Relevant = 'relevant',
  Old = 'old',
}

interface GetRelevancyParams {
  createdDate: ISOString;
  isInLastViewed: boolean;
  isBulletinType: boolean;
  dealType?: DealType;
}

export const getRelevancy = ({
  createdDate,
  isInLastViewed,
  isBulletinType,
  dealType,
}: GetRelevancyParams) => {
  if (!createdDate) return UnitRelevancy.Old;

  const daysInMarket = config.daysInMarket[dealType || DealType.Buy];
  const relevancyDays = isBulletinType ? daysInMarket.bulletinsAsRelevantInDays : daysInMarket.projectsAsRelevantInDays;
  const newDays = isBulletinType ? daysInMarket.bulletinsAsNewInDays : daysInMarket.projectsAsNewInDays;
  const passedDays: number = daysFrom(parseISODate(createdDate));
  let unitRelevancy: UnitRelevancy = passedDays > relevancyDays
    ? UnitRelevancy.Old
    : passedDays > newDays
      ? UnitRelevancy.Relevant
      : UnitRelevancy.New;

  if (isInLastViewed && unitRelevancy === UnitRelevancy.New) {
    unitRelevancy = UnitRelevancy.Relevant;
  }

  return unitRelevancy;
};

interface RelevancyLabelOwnProps {
  createdDate: ISOString;
  lastUpdatedDate?: ISOString;
  isBulletinType: boolean;
  id: PoiId;
  dealType?: DealType;
  customRenderer?: (props: { text: string }) => JSX.Element;
  shouldShowRelevancy?: boolean;
}
interface RelevancyLabelStateProps {
  isInLastViewed: boolean;
  dealType: DealType;
}

const RelevancyLabel: React.FC<RelevancyLabelOwnProps & RelevancyLabelStateProps> = (props) => {
  const { createdDate, lastUpdatedDate, isInLastViewed, dealType, isBulletinType, customRenderer, shouldShowRelevancy = true } = props;

  const daysInMarket = config.daysInMarket[dealType];

  const { _be_addLastUpdateToWeights } = useABTests();
  const { t } = useLocale();
  const marketplace = useSelector(marketplaceSelector);
  const isRecentlyViewedV2 = marketplace === MarketplaceType.Residential;
  const relevancyDays = isBulletinType ? daysInMarket.bulletinsAsRelevantInDays : daysInMarket.projectsAsRelevantInDays;
  const passedDays = createdDate ? daysFrom(parseISODate(createdDate)) : null;
  const relevancy = getRelevancy({ createdDate, isInLastViewed, isBulletinType, dealType });
  const isModeB = _be_addLastUpdateToWeights === AddLastUpdateToWeights.modeB;
  const isUpdated = new Date(lastUpdatedDate).getTime() !== new Date(createdDate).getTime();
  const passedUpdateDays = lastUpdatedDate ? daysFrom(new Date(lastUpdatedDate)) : null;

  if (isRecentlyViewedV2 && isInLastViewed) {
    return (
      <ViewedLabelWrapper>
        <ViewedIcon />
        {t('unitPage.dateLabel.viewed')}
      </ViewedLabelWrapper>
    );
  }

  if (!daysInMarket || !isBulletinType || !shouldShowRelevancy) return null;

  let text = null;

  if (!isModeB) {
    text = t('unitPage.dateLabel', { passedDays, relevancy, relevancyDays });
  }
  else if (isModeB && (
    (isUpdated && passedUpdateDays === 0) || relevancy === UnitRelevancy.New || (!isUpdated && passedDays === 0)
  )) {
    text = t('unitPage.dateLabel.updated', { relevancy });
  }

  if (!text) return null;

  return customRenderer ? customRenderer({ text }) : (
    <RelevancyLabelWrapper
      small
      data-auto="date-label"
      isNewOffer={relevancy === UnitRelevancy.New}
      isDark
    >
      {text}
    </RelevancyLabelWrapper>
  );
};

const mapStateToProps = (state: State, props: RelevancyLabelOwnProps) => ({
  isInLastViewed: isInLastViewedSelector(state, props.id),
});

export default connect(mapStateToProps)(RelevancyLabel);
